import styled, { css } from 'foundations-theming-specialty/utils';
import ErrorBoundary from '../ErrorBoundary';
import { GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_EXPANDED_WIDTH, GLOBAL_NAVIGATION_SIDEBAR_WIDTH, GLOBAL_NAVIGATION_TOOLBAR_HEIGHT } from 'nav-meta/constants';
import PinButton from './PinButton';
import { forwardRef, useCallback } from 'react';
import { ScrollAbility } from './constants';
import useVerticalNavScrollControls from './hooks/useVerticalNavScrollControls';
import { DownChevron } from 'breadcrumbs-ui/icons';
import { Z_INDEX_OVERLAY_MEDIUM } from '../../constants/z_index';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const VERTICAL_NAV_ID = 'hs-vertical-nav';
export const VERTICAL_NAV_CONTENT_ID = 'hs-vertical-nav-content';
const VNCMask = styled.div.withConfig({
  displayName: "VerticalNavContainer__VNCMask",
  componentId: "jz7a1z-0"
})(({
  $isOpen,
  theme
}) => {
  return css(["position:fixed;background-color:", ";top:", ";height:calc(100vh - ", ");left:0;z-index:", ";width:", ";overflow:visible;", ""], theme.color['global-shell-fill-surface-default'], GLOBAL_NAVIGATION_TOOLBAR_HEIGHT, GLOBAL_NAVIGATION_TOOLBAR_HEIGHT, Z_INDEX_OVERLAY_MEDIUM, GLOBAL_NAVIGATION_SIDEBAR_WIDTH, $isOpen ? '' : css(["&::after{content:'';right:0;top:0;height:100%;width:12px;position:absolute;display:block;}"]));
});
const VNCContent = styled.div.withConfig({
  displayName: "VerticalNavContainer__VNCContent",
  componentId: "jz7a1z-1"
})(["display:flex;flex-direction:column;overflow-y:auto;scrollbar-width:none;height:100%;::-webkit-scrollbar{display:none;}"]);
const VNCMain = styled.div.withConfig({
  displayName: "VerticalNavContainer__VNCMain",
  componentId: "jz7a1z-2"
})(["ul.primary-nav-menu{width:", ";& > li{width:100%;margin:0;}}.primary-nav-menu-item{& > button span,& > a span{opacity:0;visibility:hidden;height:0;}& > a{transition:none;}}&.isPinned{ul.primary-nav-menu{width:", ";overflow-y:visible;& > li{width:100%;}}.primary-nav-menu-item{& > button span,& > a span{visibility:visible;opacity:1;height:auto;}}.secondary-menu-heading{display:none;}}"], GLOBAL_NAVIGATION_SIDEBAR_WIDTH, GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_EXPANDED_WIDTH);
const VNCFooter = styled.div.withConfig({
  displayName: "VerticalNavContainer__VNCFooter",
  componentId: "jz7a1z-3"
})(["flex:1;align-content:end;width:", ";button{position:relative;opacity:1;left:50%;transform:translateX(-50%);bottom:0;margin:4px 0 8px 0;}&.isPinned{width:", ";button{opacity:1;left:100%;transform:translate(calc(-100% - 12px));}}"], GLOBAL_NAVIGATION_SIDEBAR_WIDTH, GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_EXPANDED_WIDTH);
const UpStyles = theme => {
  return css(["top:0;border-bottom:", " solid ", ";"], theme.borderWidth['100'], theme.color['global-shell-border-default']);
};
const DownStyles = theme => {
  return css(["bottom:0;border-top:", " solid ", ";"], theme.borderWidth['100'], theme.color['global-shell-border-default']);
};
const StyledScrollArrow = styled.div.withConfig({
  displayName: "VerticalNavContainer__StyledScrollArrow",
  componentId: "jz7a1z-4"
})(({
  direction,
  theme
}) => {
  return css(["position:absolute;left:50%;transform:translate(-50%,0);background:", ";border-bottom:", " solid ", ";width:100%;height:32px;display:flex;justify-content:center;align-content:center;z-index:1;", ";& > svg{width:16px;fill:", ";transform:", ";display:flex;}"], theme.color['global-shell-fill-surface-default'], theme.borderWidth['100'], theme.color['global-shell-border-default'], direction === 'up' ? UpStyles(theme) : DownStyles(theme), theme.color['global-shell-icon-nav-item-default'], direction === 'up' ? 'rotate(180deg)' : 'none');
});
const VerticalNavContainer = /*#__PURE__*/forwardRef(({
  children,
  isFocusInside,
  updateIsInteractingWithVerticalNav,
  isPinned,
  setIsPinned,
  tempPauseOutsideAlerter
}, ref) => {
  const className = isPinned ? 'isPinned' : '';
  const {
    setContentRef,
    showTopScroll,
    showDownScroll,
    startScrolling,
    onScroll
  } = useVerticalNavScrollControls();
  const scrollUp = useCallback(() => {
    startScrolling(ScrollAbility.up);
  }, [startScrolling]);
  const scrollDown = useCallback(() => {
    startScrolling(ScrollAbility.down);
  }, [startScrolling]);
  const stopScroll = useCallback(() => {
    startScrolling(false);
  }, [startScrolling]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "Vertical Nav",
    children: /*#__PURE__*/_jsx(VNCMask, {
      ref: ref,
      id: VERTICAL_NAV_ID,
      $isOpen: !!isFocusInside,
      "data-test-id": VERTICAL_NAV_ID,
      className: className,
      children: /*#__PURE__*/_jsxs(VNCContent, {
        ref: setContentRef,
        onFocus: updateIsInteractingWithVerticalNav(true),
        onMouseEnter: updateIsInteractingWithVerticalNav(true),
        onMouseLeave: !isPinned ? updateIsInteractingWithVerticalNav(false) : undefined,
        onScroll: onScroll,
        onBlur: updateIsInteractingWithVerticalNav(false),
        "data-test-id": VERTICAL_NAV_CONTENT_ID,
        "data-onboarding-hook": "primary-menu",
        children: [/*#__PURE__*/_jsxs(VNCMain, {
          "data-test-id": "VNCMain",
          className: className,
          "data-onboarding-hook": "primary-menu-main",
          children: [showTopScroll ? /*#__PURE__*/_jsx(StyledScrollArrow, {
            direction: "up",
            role: "none",
            onMouseEnter: scrollUp,
            onMouseLeave: stopScroll,
            onBlur: stopScroll,
            children: /*#__PURE__*/_jsx(DownChevron, {})
          }) : null, children, showDownScroll ? /*#__PURE__*/_jsx(StyledScrollArrow, {
            role: "none",
            direction: "down",
            onMouseEnter: scrollDown,
            onMouseLeave: stopScroll,
            onBlur: stopScroll,
            children: /*#__PURE__*/_jsx(DownChevron, {})
          }) : null]
        }), /*#__PURE__*/_jsx(VNCFooter, {
          "data-onboarding-hook": "primary-menu-footer",
          className: className,
          children: /*#__PURE__*/_jsx(PinButton, {
            isPinned: isPinned,
            setIsPinned: setIsPinned,
            tempPauseOutsideAlerter: tempPauseOutsideAlerter
          })
        })]
      })
    })
  });
});
export default VerticalNavContainer;